/**
 * Level-controlled logger utility.
 *
 * Production default: "warn" — only warnings and errors appear.
 * Dev default: "debug" — everything appears.
 *
 * Override at runtime (even in production):
 *   localStorage.setItem('LOG_LEVEL', 'debug')
 *   // then reload the page
 */

type LogLevel = "debug" | "info" | "warn" | "error" | "none";

const LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  none: 4,
};

function getLogLevel(): LogLevel {
  try {
    const override = localStorage.getItem("LOG_LEVEL");
    if (override && override in LEVEL_PRIORITY) return override as LogLevel;
  } catch {
    // localStorage may be unavailable (SSR, incognito, etc.)
  }
  return import.meta.env.PROD ? "warn" : "debug";
}

function shouldLog(level: LogLevel): boolean {
  return LEVEL_PRIORITY[level] >= LEVEL_PRIORITY[getLogLevel()];
}

export const log = {
  debug: (tag: string, ...args: unknown[]) => {
    if (shouldLog("debug")) console.debug(`[${tag}]`, ...args);
  },
  info: (tag: string, ...args: unknown[]) => {
    if (shouldLog("info")) console.log(`[${tag}]`, ...args);
  },
  warn: (tag: string, ...args: unknown[]) => {
    if (shouldLog("warn")) console.warn(`[${tag}]`, ...args);
  },
  error: (tag: string, ...args: unknown[]) => {
    if (shouldLog("error")) console.error(`[${tag}]`, ...args);
  },
};
